import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import { Menu, DropdownMenu, MenuLink, MenuItem } from '../Menus';

import DropdownButton from './DropdownButton_DEPRECATED';

import './styles/buttonSplit.css';

// This component is deprecated in favor of the <MenuButton/> from the __pds folder

const ButtonSplit = ({ primaryAction, type, menuItems, menuAriaLabel }) => {
  const actionTypes = {
    link: 'LINK',
    button: 'BUTTON',
  };

  const typeStyle = `button-split button-split--${type}`;

  const classes = 'button-split--action';

  const pendoTrack = (name, data) => {
    // only operate if the Pendo object/library is loaded
    /* eslint-disable no-undef */
    if (typeof pendo !== 'undefined' && pendo.isReady && pendo.isReady()) {
      return pendo.track(name, data);
    }
    /* eslint-enable no-undef */
    setTimeout(() => {
      pendoTrack(name, data);
    }, 500);

    return null;
  };

  // handles the click by when a callback is passed in, by combining the callback with with the pendo click tracking for both primary actions and menu items.
  const handleClick = (label, itemType, callback = null) => {
    return () => {
      pendoTrack('Button – split', {
        designToolkit: true,
        pattern: 'button - split',
        action: itemType,
        element: label,
        url: window.location.href,
      });

      if (callback) {
        callback();
      }
    };
  };

  // renders the primary action item, it returns either a button, anchor, or Link.
  const renderPrimaryAction = ({
    asLink = false,
    callback = null,
    href = null,
    label,
  }) => {
    let actionType;

    if (asLink && href) {
      actionType = actionTypes.link;
    } else if (callback) {
      actionType = actionTypes.button;
    }

    switch (actionType) {
      case actionTypes.link:
        return (
          <Link
            className={classes}
            onClick={handleClick(label, 'primary action')}
            to={href}
          >
            {label}
          </Link>
        );
      case actionTypes.button:
        return (
          <button
            className={classes}
            onClick={handleClick(label, 'primary action', callback)}
            type="button"
          >
            {label}
          </button>
        );
      default:
        return (
          <a
            className={classes}
            href={href}
            onClick={handleClick(label, 'primary action')}
          >
            {label}
          </a>
        );
    }
  };

  // renders the menu items
  const renderMenuItem = ({ callback = null, href = null, label }) => {
    if (callback) {
      return (
        <MenuItem
          key={label}
          onSelect={handleClick(label, 'menu item', callback)}
        >
          {label}
        </MenuItem>
      );
    }

    return (
      <MenuLink
        href={href}
        key={label}
        onClick={handleClick(label, 'menu item')}
      >
        {label}
      </MenuLink>
    );
  };

  return (
    <span className={typeStyle}>
      {renderPrimaryAction(primaryAction)}
      <Menu>
        <DropdownButton
          aria-label={menuAriaLabel}
          className="button-split--trigger"
          onClick={handleClick(primaryAction.label, 'menu')}
          type="subtle"
        />
        <DropdownMenu size="small">
          {renderMenuItem(primaryAction)}
          {menuItems.map((item) => renderMenuItem(item))}
        </DropdownMenu>
      </Menu>
    </span>
  );
};

ButtonSplit.defaultProps = {
  menuAriaLabel: 'Toggle menu',
  type: 'secondary',
};

ButtonSplit.propTypes = {
  /**
   * Accessibility text for the secondary action menu trigger
   */
  menuAriaLabel: PropTypes.string,
  /**
   * Array of objects describing each secondary action
   */
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      callback: PropTypes.func,
      href: PropTypes.string,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  /**
   * Object describing the primary action
   */
  primaryAction: PropTypes.shape({
    asLink: PropTypes.bool,
    callback: PropTypes.func,
    href: PropTypes.string,
    label: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * The style type of button
   */
  type: PropTypes.oneOf(['primary', 'secondary']),
};

export default ButtonSplit;
